import React from 'react'
import { useRouter } from 'next/router'
import cx from 'classnames'
import styles from './Button.module.scss'
import { ButtonProps, ClickEvt } from '~types'
import { getFevoWidgetNameFromUrl, isExternalLink, openFevoWidget } from 'utils'
import Link from 'next/link'
import { fireAnalyticsEvent, getGtmClickSource } from '~analyticsUtils'
import { useModal } from 'utils/hooks/useModal'
import { Modal } from '~elements'

const Button = ({
  analytics,
  color = 'light',
  disabled = false,
  fill = 'solid',
  isFullWidth = false,
  isTargetBlank = false,
  justify = 'left',
  modal,
  onClick,
  size = 'md',
  title,
  url = null
}: ButtonProps) => {
  const { isModalOpen, openModal, closeModal } = useModal()

  const wrapperClasseNames = cx([
    styles['btn-wrapper'],
    styles[`btn-wrapper--justify-contents-${justify}`],
    styles[isFullWidth ? 'btn-wrapper--is-full-width' : '']
  ])

  const buttonClassNames = cx([
    styles['btn'],
    styles[`btn--${size}`],
    styles[`btn--${color}`],
    styles[`btn--fill-${fill}`],
    styles[disabled ? `btn--is-disabled` : ''],
    styles[isFullWidth ? 'btn--is-full-width' : '']
  ])

  const { asPath } = useRouter()
  const fevoWidgetName = url ? getFevoWidgetNameFromUrl(url) : ''

  const clickHandler = (e: ClickEvt, title: string) => {
    if (disabled) return false

    if (onClick) onClick(e)

    if (fevoWidgetName) openFevoWidget(fevoWidgetName, e)

    // GTM Analytics event
    const {
      clickTracking = '',
      eventData,
      linkLocation = '',
      linkModule = '',
      linkPosition = '',
      skipEvent = false
    } = analytics || {}

    const { eventCategory, eventDate, eventType, ticketmasterEventId } = eventData || {}

    if (!skipEvent) {
      fireAnalyticsEvent(url, asPath, {
        clickSource: getGtmClickSource(asPath),
        clickTracking: clickTracking ? clickTracking : `${linkModule}:${title}:cta`,
        clickType: 'cta',
        linkLocation: linkLocation,
        linkModule: linkModule,
        linkPosition: linkPosition,
        eventData: {
          eventDate: eventDate,
          eventType: eventType,
          eventCategory: eventCategory,
          ticketmasterEventId: ticketmasterEventId
        }
      })
    }

    // @TODO: make below work with GTM, leaving in for reference
    if (url && isTargetBlank) {
      // const ga = window[window['GoogleAnalyticsObject']]
      // let gaLinkerDecoratedUrl = requestCameFromUrl
      // if (ga && typeof ga.getAll === 'function') {
      //   const tracker = ga.getAll()[0]
      //   gaLinkerDecoratedUrl = new window.gaplugins.Linker(tracker).decorate(requestCameFromUrl)
      // }
      // window.open(gaLinkerDecoratedUrl, '_blank')
    }
  }

  const isExternal = url && isExternalLink(url)

  const sharedProps = {
    className: buttonClassNames,
    onClick: (e: ClickEvt) => clickHandler(e, title)
  }

  const renderButton = () => {
    if (url && !fevoWidgetName && !disabled) {
      if (isTargetBlank || isExternal) {
        return (
          <a
            {...sharedProps}
            href={url}
            {...(isExternal && { rel: 'noreferrer' })}
            {...(isTargetBlank && { target: '_blank' })}
          >
            {title}
          </a>
        )
      } else {
        return (
          <Link legacyBehavior href={url}>
            <a {...sharedProps} href={url}>
              {title}
            </a>
          </Link>
        )
      }
    } else if (fevoWidgetName || onClick || disabled || modal) {
      return (
        <button {...sharedProps} {...(disabled && { disabled })} {...(modal && { onClick: openModal })}>
          {title}
        </button>
      )
    }
    return null
  }

  if (modal && modal?.type === 'form' && modal?.form) {
    modal.form.onCloseParentModal = closeModal
  }

  return (
    <div className={wrapperClasseNames}>
      {renderButton()}
      {modal && <Modal {...modal} isOpen={isModalOpen} handleModalClose={closeModal} />}
    </div>
  )
}

export default Button
